/**
 * Generates HTML email template for "Quote Formally Accepted".
 * Sent when the client signs the contract and proceeds to checkout.
 * Uses shared layout from emailLayout.js for header/footer.
 */

import { formatUTCToDateTime } from '@/utils/formatters';
import { getAirportTimezone } from '@/utils/airportTimezone';
import { icon, spacer, emailDocOpen, emailDocClose, emailHeader, emailFooter } from '../emailLayout';
import { getAircraftPhotos } from '@/features/tails/utils';
import { formatPrice, trunc2 } from '@/utils';

const getTripTypeLabel = (legs) => {
  if (!legs || legs.length === 0) return '';
  if (legs.length === 1) return 'One Way';
  if (legs.length === 2) {
    const first = legs[0];
    const second = legs[1];
    if (
      first.departure_airport === second.arrival_airport &&
      first.arrival_airport === second.departure_airport
    ) {
      return 'Round Trip';
    }
  }
  return 'Multi-Leg';
};

const getRouteDisplay = (legs) => {
  if (!legs || legs.length === 0) return '';
  if (legs.length === 1) return `${legs[0].departure_airport || ''} → ${legs[0].arrival_airport || ''}`;
  return legs.map(l => l.departure_airport).concat(legs[legs.length - 1].arrival_airport).filter(Boolean).join(' → ');
};

const getModelName = (aircraft) => {
  if (!aircraft) return '';
  if (aircraft.aircraft_make_model) {
    const { manufacturer } = aircraft.aircraft_make_model;
    const makeModel = aircraft.make_model || aircraft.aircraft_make_model.make_model || '';
    if (manufacturer && makeModel) {
      const modelPart = makeModel.replace(new RegExp(`^${manufacturer}[- ]*`, 'i'), '');
      return `${manufacturer} ${modelPart}`.trim();
    }
    return makeModel || '';
  }
  return aircraft.make_model || aircraft.tail_number || '';
};

/**
 * Build the quote accepted email HTML
 *
 * @param {Object} params
 * @param {Object} params.quote - Full quote object
 * @param {Object} params.selectedBidOption - The accepted bid option
 * @param {string} params.signedBy - Full name used for digital signature
 * @param {string} [params.organizationName] - Organization name
 * @returns {string} HTML email body
 */
export const buildQuoteAcceptedEmailHtml = ({
  quote,
  selectedBidOption,
  signedBy,
  organizationName,
  organization
}) => {
  const client = quote?.client;
  const clientName = [client?.first_name, client?.last_name].filter(Boolean).join(' ') || 'Valued Client';
  const legs = quote?.bid_items?.[0]?.bid?.leg_itinerary || [];
  const aircraft = selectedBidOption?.aircraft;
  const operator = selectedBidOption?.operator;

  const tripType = getTripTypeLabel(legs);
  const routeDisplay = getRouteDisplay(legs);
  const departureDates = legs.map((leg, i) => {
    const tz = getAirportTimezone(leg.departure_airport);
    const date = leg.departure_date ? formatUTCToDateTime(leg.departure_date, tz, 'dddd, MMMM D, YYYY') : '';
    const time = leg.departure_date ? formatUTCToDateTime(leg.departure_date, tz, 'HH:mm') : '';
    return {
      route: `${leg.departure_airport || ''} → ${leg.arrival_airport || ''}`,
      date,
      time,
      label: legs.length > 1 ? `Leg ${i + 1}` : 'Departure Date',
    };
  }).filter(d => d.date);
  const passengerCount = quote?.passenger_count || quote?.bid_items?.[0]?.bid?.passenger_count || '';

  // Aircraft details
  const modelName = getModelName(aircraft);
  const imageUrl = getAircraftPhotos(aircraft);
  const operatorName = operator?.name || '';
  const operatorLocation = [operator?.city, operator?.state].filter(Boolean).join(', ');
  const operatorDisplay = operatorLocation ? `${operatorName} • ${operatorLocation}` : operatorName;
  const price = formatPrice(trunc2(selectedBidOption?.total_price || selectedBidOption?.final_price));

  // Badges
  const badges = [];
  if (operator?.argus_rating && operator.argus_rating !== 'No') {
    badges.push({ label: operator.argus_rating.toUpperCase(), bg: '#F3F4F6', color: '#374151', iconName: 'trophy' });
  }
  if (operator?.wyvern_rating && operator.wyvern_rating !== 'No') {
    badges.push({ label: 'WINGMAN', bg: '#F3F4F6', color: '#374151', iconName: 'medal' });
  }
  if (aircraft?.aircraft_features?.no_aog) {
    badges.push({ label: 'NO AOG', bg: '#DCFCE7', color: '#15803D', iconName: 'check' });
  }

  const badgesHtml = badges.map(b =>
    `<span style="display:inline-block;padding:4px 10px;background:${b.bg};color:${b.color};font-size:11px;font-weight:600;border-radius:16px;margin-right:6px;margin-bottom:4px;">${icon(b.iconName, 14, b.color)} ${b.label}</span>`
  ).join('');

  // Amenities
  const amenities = aircraft?.aircraft_features?.custom_features || [];
  const amenitiesHtml = amenities.map(a =>
    `<span style="display:inline-block;padding:4px 12px;background:#F3F4F6;color:#374151;font-size:12px;font-weight:500;border-radius:16px;margin-right:6px;margin-bottom:6px;">${icon('check', 14, '#374151')} ${a}</span>`
  ).join('');

  // Quote validity
  const expiryRaw = quote?.expiry_date || quote?.valid_until;
  const validUntil = expiryRaw ? new Date(expiryRaw).toLocaleDateString('en-US', {
    month: 'long', day: 'numeric', year: 'numeric',
  }) : '';

  return `
${emailDocOpen({ title: 'Quote Formally Accepted' })}
${emailHeader({ title: 'Quote Formally Accepted', organization, isBroker: true })}

        ${spacer()}  

        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;background-color:#FFFFFF;overflow:hidden;border:1px solid #EBEBEB;">
          <!-- ===== CLIENT / QUOTE NUMBER row ===== -->
          <tr>
            <td style="padding:16px 32px;">
              <table role="presentation" width="100%" cellpadding="0" cellspacing="0">
                <tr>
                  <td>
                    <span style="font-size:12px;text-transform:uppercase;color:#6B7280;letter-spacing:0.5px;font-weight:600;">Client</span><br/>
                    <span style="font-size:15px;font-weight:600;color:#111827;">${clientName}</span>
                  </td>
                  <td align="right">
                    <span style="font-size:12px;text-transform:uppercase;color:#6B7280;letter-spacing:0.5px;font-weight:600;">Company</span><br/>
                    <span style="font-size:15px;font-weight:600;color:#111827;">${organizationName || '-'}</span>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
          </table>

        ${spacer()}

        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;background-color:#FFFFFF;overflow:hidden;border:1px solid #EBEBEB;">

          <!-- ===== CONFIRMATION MESSAGE ===== -->
          <tr>
            <td style="padding:28px 32px 24px;">
              <table role="presentation" width="100%" cellpadding="0" cellspacing="0">
                <tr>
                  <td width="56" valign="top" style="padding-right:16px;">
                    <div style="width:48px;height:48px;background-color:#DCFCE7;border-radius:50%;text-align:center;line-height:48px;">
                      ${icon('check', 24, '#16A34A')}
                    </div>
                  </td>
                  </tr>
                  <tr>
                  <td valign="middle" style="padding-top:12px;">
                    <p style="margin:0;font-size:14px;color:#374151;line-height:1.6;">
                      The quote for ${clientName} has been accepted and confirmed. Kindly review the selected aircraft below.
                    </p>
                  </td>
                </tr>
              </table>
            </td>
          </tr>

          </table>

        ${spacer()}

        <!-- ===== TRIP DETAILS ===== -->
        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;background-color:#FFFFFF;overflow:hidden;border:1px solid #EBEBEB;border-bottom:none;">
          <tr>
            <td style="padding:20px 32px 8px;">
              <span style="font-size:12px;text-transform:uppercase;color:#6B7280;letter-spacing:0.5px;font-weight:600;">Trip Details</span>
            </td>
          </tr>
          <!-- Row 1: Passenger Count + Route -->
          <tr>
            ${passengerCount ? `
            <td style="padding:12px 32px;" valign="top" width="50%">
              <table role="presentation" cellpadding="0" cellspacing="0">
                <tr>
                  <td width="44" valign="top" style="padding-right:12px;">
                    <div style="width:40px;height:40px;background-color:#F3F4F6;border-radius:50%;text-align:center;line-height:40px;">
                      ${icon('team', 20, '#6B7280')}
                    </div>
                  </td>
                  <td valign="middle">
                    <p style="margin:0 0 2px;font-size:14px;color:#6B7280;">Passenger Count</p>
                    <p style="margin:0;font-size:18px;font-weight:600;color:#111827;">${passengerCount}</p>
                  </td>
                </tr>
              </table>
            </td>` : ''}
            <td style="padding:12px 32px;" valign="top" ${passengerCount ? 'width="50%"' : ''}>
              <table role="presentation" cellpadding="0" cellspacing="0">
                <tr>
                  <td width="44" valign="top" style="padding-right:12px;">
                    <div style="width:40px;height:40px;background-color:#F3F4F6;border-radius:50%;text-align:center;line-height:40px;">
                      ${icon('plane', 20, '#6B7280')}
                    </div>
                  </td>
                  <td valign="middle">
                    <p style="margin:0 0 2px;font-size:14px;color:#6B7280;">${tripType || 'Trip'}</p>
                    <p style="margin:0;font-size:18px;font-weight:600;color:#111827;">${routeDisplay}</p>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
          <!-- Row 2: Departure Date(s) -->
          ${departureDates.map((d, i) => `
          <tr>
            <td style="padding:12px 32px ${i === departureDates.length - 1 ? '20px' : '0'};" valign="top" ${passengerCount ? 'colspan="2"' : ''}>
              <table role="presentation" cellpadding="0" cellspacing="0">
                <tr>
                  <td width="44" valign="top" style="padding-right:12px;">
                    <div style="width:40px;height:40px;background-color:#F3F4F6;border-radius:50%;text-align:center;line-height:40px;">
                      ${icon('calendar', 20, '#6B7280')}
                    </div>
                  </td>
                  <td valign="middle">
                    <p style="margin:0 0 2px;font-size:14px;color:#6B7280;">${d.label}${departureDates.length > 1 ? ` (${d.route})` : ''}</p>
                    <p style="margin:0;font-size:18px;font-weight:600;color:#111827;">${d.date}${d.time ? ` at ${d.time}` : ''}</p>
                  </td>
                </tr>
              </table>
            </td>
          </tr>`).join('')}

        </table>
        
        ${validUntil ? `
         <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;background-color:#FFFFFF;overflow:hidden;border:1px solid #EBEBEB;border-top:none;">
            <tr>
            <td style="padding:12px 32px 20px;">
              <table role="presentation" width="100%" cellpadding="0" cellspacing="0">
                <tr>
                  <td style="background-color:#e7e7e7;border-radius:20px;padding:3px 0;text-align:center;">
                    <span style="font-size:12px;color:#374151;">${icon('calendar', 14, '#9CA3AF')} Expires on ${validUntil}</span>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
         </table>` : ''}

        ${spacer()}

        <!-- ===== AIRCRAFT IMAGE + DETAILS ===== -->
        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;background-color:#FFFFFF;overflow:hidden;border:1px solid #EBEBEB;">
          ${imageUrl ? `
          <tr>
            <td style="padding:0;line-height:0;font-size:0;">
              <img src="${imageUrl}" alt="${modelName}" width="600" style="display:block;width:100%;height:auto;object-fit:cover;max-height:300px;" />
            </td>
          </tr>` : ''}
          <tr>
            <td style="padding:20px 32px;">
              <h2 style="margin:0 0 4px;font-size:20px;font-weight:500;color:#111827;">${aircraft?.tail_number || ''}</h2>
              <p style="margin:0 0 2px;font-size:13px;color:#6B7280;">${icon('plane', 14, '#6B7280')} ${modelName}</p>
              ${operatorDisplay ? `<p style="margin:0 0 12px;font-size:13px;color:#6B7280;">${icon('building', 14, '#6B7280')} ${operatorDisplay}</p>` : ''}

              <!-- Price Badge -->
              <div style="display:inline-block;padding:6px 14px;background-color:#E8F4FD;border-radius:16px;margin-bottom:12px;">
                <span style="font-size:14px;font-weight:700;color:#0C3E71;">${icon('priceTag', 14, '#0C3E71')} ${price}</span>
              </div>

              <!-- Rating Badges -->
              ${badgesHtml ? `<div style="margin-top:8px;">${badgesHtml}</div>` : ''}

              <!-- About -->
              ${aircraft?.description ? `
              <div style="margin-top:16px;">
                <h3 style="margin:0 0 6px;font-size:13px;font-weight:600;color:#6B7280;">About the Aircraft</h3>
                <p style="margin:0;font-size:13px;color:#6B7280;line-height:1.5;">${aircraft.description}</p>
              </div>` : ''}

              <!-- Amenities -->
              ${amenitiesHtml ? `
              <div style="margin-top:16px;">
                <h3 style="margin:0 0 8px;font-size:13px;font-weight:600;color:#6B7280;">Amenities</h3>
                <div>${amenitiesHtml}</div>
              </div>` : ''}
            </td>
          </tr>
        </table>

        <!-- ===== QUOTE VALIDITY NOTE ===== -->
        ${validUntil ? `
        ${spacer()}

        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;background-color:#efefef;overflow:hidden;">
          <tr>
            <td style="padding:16px 32px;">
                <p style="margin:0;font-size:12px;line-height:1.5;color: #4c4c4c;">
                  This quote is valid until <strong>${validUntil}</strong>. Prices are subject to availability and may change.
                </p>
            </td>
          </tr>
        </table>` : ''}

        ${spacer()}

${emailFooter({ organization })}
${emailDocClose()}`;
};
